import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import axios from "axios";
import { styled } from "@mui/material/styles";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// API Constants
const API_BASE_URL = "https://api.ipaisa.co.in/myapp/admin";
const API_ENDPOINTS = {
  ADD_REMARK: `${API_BASE_URL}/addRemark`,
  GET_ALL_REMARKS: `${API_BASE_URL}/getAllRemark`,
  DELETE_REMARK: `${API_BASE_URL}/deleteRemark`,
};

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: "16px",
  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
  background: theme.palette.background.paper,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
  },
});

const FtrRemarks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State Management
  const [remark, setRemark] = useState("");
  const [remarksList, setRemarksList] = useState([]);
  const [isLoading, setIsLoading] = useState({
    general: false,
    add: false,
    update: false,
    delete: false,
  });
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");

  const showSnackbar = (message, severity = "success") => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  // API Handlers (same as before)
  const fetchRemarks = async () => {
    const token = sessionStorage.getItem("jwtToken")
    try {
      setIsLoading((prev) => ({ ...prev, general: true }));
      const response = await axios.get(API_ENDPOINTS.GET_ALL_REMARKS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.statusCodeValue === 200) {
        const formattedRemarks = response.data.body.data.map((remark) => ({
          id: remark.id,
          text: remark.remark,
          date: response.data.body.timestamp,
        }));
        setRemarksList(formattedRemarks);
      } else {
        throw new Error(
          response.data.body?.message || "Failed to fetch remarks"
        );
      }
    } catch (error) {
      console.error("Error fetching remarks:", error);
      showSnackbar("Failed to load remarks", "error");
      setRemarksList([]);
    } finally {
      setIsLoading((prev) => ({ ...prev, general: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!remark.trim()) return;
    const token = sessionStorage.getItem("jwtToken")
    try {
      setIsLoading((prev) => ({ ...prev, add: true }));
      const response = await axios.post(
        API_ENDPOINTS.ADD_REMARK,
        { remark: remark },
        {
          headers: {
            "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.statusCodeValue === 200) {
        const newRemark = {
          id: response.data.body.data.id,
          text: response.data.body.data.remark,
          date: response.data.body.timestamp,
        };

        setRemarksList([newRemark, ...remarksList]);
        setRemark("");
        showSnackbar(response.data.body.message || "Remark added successfully");
      } else {
        throw new Error(response.data.body?.message || "Failed to add remark");
      }
    } catch (error) {
      console.error("Error submitting remark:", error);
      showSnackbar(
        error.response?.data?.body?.message ||
          error.message ||
          "Failed to add remark. Please try again.",
        "error"
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, add: false }));
    }
  };

  const startEditing = (id, text) => {
    setEditingId(id);
    setEditText(text);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleUpdate = async (id) => {
    if (!editText.trim()) return;

    try {
      setIsLoading((prev) => ({ ...prev, update: true }));
      // TODO: Replace with actual API call when endpoint is available
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call

      setRemarksList(
        remarksList.map((item) =>
          item.id === id
            ? {
                ...item,
                text: editText,
                date: new Date().toISOString(),
              }
            : item
        )
      );
      setEditingId(null);
      showSnackbar("Remark updated successfully");
    } catch (error) {
      console.error("Error updating remark:", error);
      showSnackbar("Failed to update remark", "error");
    } finally {
      setIsLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
        const token = sessionStorage.getItem("jwtToken")
    try {
      setIsLoading((prev) => ({ ...prev, delete: true }));

      const response = await axios.delete(
        `${API_ENDPOINTS.DELETE_REMARK}?id=${deleteId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.statusCodeValue === 200) {
        setRemarksList(remarksList.filter((item) => item.id !== deleteId));
        showSnackbar(
          response.data.body.message || "Remark deleted successfully"
        );
      } else {
        throw new Error(
          response.data.body?.message || "Failed to delete remark"
        );
      }
    } catch (error) {
      console.error("Error deleting remark:", error);
      showSnackbar(
        error.response?.data?.body?.message ||
          error.message ||
          "Failed to delete remark. Please try again.",
        "error"
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, delete: false }));
      setOpenDeleteDialog(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };

  useEffect(() => {
    fetchRemarks();
  }, []);

  return (
    <Box
      sx={{
        padding: isMobile ? "1rem" : "2rem",
        marginTop: "calc(3% + 56px)",
        width: "100%",
        overflowX: "auto",
      }}
    >
      <StyledPaper>
        <Snackbar
          open={snackOpen}
          autoHideDuration={4000}
          onClose={handleSnackClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            onClose={handleSnackClose}
            severity={snackSeverity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {snackMessage}
          </MuiAlert>
        </Snackbar>

        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Remarks
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" }, // Stack vertically on mobile, horizontally on larger screens
              alignItems: { xs: "flex-start", sm: "center" }, // Align items to the start on mobile, center on larger screens
              gap: 2, // Space between elements
            }}
          >
            <StyledTextField
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              label="Add your remark"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              sx={{ flexGrow: 1, width: { xs: "100%", sm: "50%" } }} // Full width on mobile, 50% on larger screens
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!remark.trim() || isLoading.add}
              sx={{
                borderRadius: "12px",
                px: 2, // Adjust padding for a smaller button
                py: 1,
                textTransform: "none",
                fontSize: "1rem",
                height: "fit-content", // Ensure the button height fits the content
                minWidth: { xs: "100%", sm: "auto" }, // Full width on mobile, auto on larger screens
              }}
              startIcon={
                isLoading.add ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {isLoading.add ? "Posting..." : "Post Remark"}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Previous Remarks
        </Typography>

        {isLoading.general && remarksList.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : remarksList.length === 0 ? (
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            py={4}
          >
            No remarks yet. Be the first to add one!
          </Typography>
        ) : isMobile ? (
          // Mobile view - list layout
          <Box>
            {remarksList.map((item, index) => (
              <Paper key={item.id} sx={{ mb: 2, p: 2, borderRadius: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">{index + 1})</Typography>
                </Box>
                {editingId === item.id ? (
                  <StyledTextField
                    fullWidth
                    multiline
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                ) : (
                  <Typography paragraph sx={{ mb: 1 }}>
                    {item.text}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  {editingId === item.id ? (
                    <>
                      <IconButton onClick={cancelEditing} size="small">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleUpdate(item.id)}
                        disabled={!editText.trim() || isLoading.update}
                        size="small"
                      >
                        {isLoading.update ? (
                          <CircularProgress size={16} />
                        ) : (
                          <CheckIcon fontSize="small" />
                        )}
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        onClick={() => startEditing(item.id, item.text)}
                        sx={{ color: "primary.main" }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(item.id)}
                        sx={{ color: "error.main" }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Stack>
              </Paper>
            ))}
          </Box>
        ) : (
          // Desktop view - table layout
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 2, boxShadow: 1 }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#027bfe" }}>
                  <TableCell sx={{ color: "white" }}>
                    <strong>Sr No.</strong>
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>
                    <strong>Remark</strong>
                  </TableCell>
                  <TableCell align="right" sx={{ color: "white" }}>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {remarksList.map((item, index) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {editingId === item.id ? (
                        <StyledTextField
                          fullWidth
                          multiline
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          size="small"
                        />
                      ) : (
                        item.text
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {editingId === item.id ? (
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          <IconButton onClick={cancelEditing}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleUpdate(item.id)}
                            disabled={!editText.trim() || isLoading.update}
                          >
                            {isLoading.update ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          <IconButton
                            onClick={() => startEditing(item.id, item.text)}
                            sx={{ color: "primary.main" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(item.id)}
                            sx={{ color: "error.main" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleDeleteCancel}
          fullScreen={isMobile}
        >
          <DialogTitle>Delete Remark</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this remark?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isLoading.delete}
            >
              {isLoading.delete ? <CircularProgress size={24} /> : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </StyledPaper>
    </Box>
  );
};

export default FtrRemarks;
